import { useCallback, useEffect, useState } from "react";
import {
  type ConversationConnect,
  activePlatformConversationId,
  cancelConversation,
  conversationConnectPath,
  createPlatformConversation,
} from "~/services/conversations";
import { apiClient } from "~/utils/apiClient";

interface PlatformSession {
  conversationId: string;
  connect: ConversationConnect;
}

export interface PlatformSessionState {
  session: PlatformSession | null;
  error: unknown;
  // Opens a new session; the old one stays for feedback and admin review.
  clearChat: () => void;
}

// Resolves the caller's active help session, opening one if needed, with its
// `/connect` bootstrap. Not SWR: a cached snapshot would rebuild the chat from
// stale history.
export function usePlatformSession(): PlatformSessionState {
  // Bumped by `clearChat`; non-zero means open a fresh conversation.
  const [generation, setGeneration] = useState(0);
  const [session, setSession] = useState<PlatformSession | null>(null);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    setSession(null);
    setError(null);

    const open = async () => {
      const existing = generation === 0 ? await activePlatformConversationId(signal) : null;
      const conversationId = existing ?? (await createPlatformConversation(signal));
      const connect = await apiClient<ConversationConnect>(
        conversationConnectPath(conversationId),
        {
          signal,
        }
      );
      if (!signal.aborted) {
        setSession({ conversationId, connect });
      }
    };

    open().catch((err) => {
      if (signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      console.error("Failed to open a platform help session", err);
      setError(err);
    });

    return () => controller.abort();
  }, [generation]);

  const clearChat = useCallback(() => {
    setGeneration((n) => n + 1);
    // Nothing will read the abandoned run's answer, and a no-op cancel is a 204.
    if (session) {
      cancelConversation(session.conversationId).catch((err) =>
        console.error("Failed to stop the cleared help session's run", err)
      );
    }
  }, [session]);

  return { session, error, clearChat };
}
